import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

import { PageHeading, Panel } from '@/components/primitives/Panel';
import { ApiError } from '@/lib/api-client';

interface Described {
  heading: string;
  message: string;
  code: string | null;
  requestId: string | null;
}

function describe(error: unknown): Described {
  if (error instanceof ApiError) {
    return {
      heading: error.isAuthFailure ? 'Not permitted' : 'The dashboard could not load this page',
      message: error.message,
      code: error.code,
      requestId: error.requestId,
    };
  }
  if (isRouteErrorResponse(error)) {
    return {
      heading: `${String(error.status)} ${error.statusText}`,
      message: typeof error.data === 'string' ? error.data : 'The route could not be resolved.',
      code: null,
      requestId: null,
    };
  }
  if (error instanceof Error) {
    return {
      heading: 'The dashboard could not load this page',
      message: error.message,
      code: null,
      requestId: null,
    };
  }
  return {
    heading: 'The dashboard could not load this page',
    message: 'An unexpected error occurred.',
    code: null,
    requestId: null,
  };
}

/**
 * Per-route `errorElement`. Keeps the AppShell chrome mounted — a failed page
 * must not take the navigation down with it — and offers a real retry.
 *
 * Retry re-runs the current entry (`navigate(0)`) rather than calling
 * `revalidate()`: these are render/lazy-import failures, not loader failures,
 * and a revalidation would not remount the broken subtree.
 */
export function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { heading, message, code, requestId } = describe(error);

  return (
    <div className="space-y-3.5">
      <PageHeading title={heading} note="This page only — the rest of the dashboard is fine." />
      <Panel title="Error" tone="crit" variant="outlined">
        <p className="text-base text-text-secondary">{message}</p>
        {code === null ? null : (
          <p className="mt-2 font-mono text-2xs text-text-muted">code: {code}</p>
        )}
        {requestId === null ? null : (
          <p className="font-mono text-2xs text-text-muted">request-id: {requestId}</p>
        )}
        <button
          type="button"
          onClick={() => {
            void navigate(0);
          }}
          className="mt-4 rounded bg-accent px-3 py-1.5 text-sm font-bold text-text-on-dark"
        >
          Retry
        </button>
      </Panel>
    </div>
  );
}
